import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';

import Utility from '../../../../../../../util/utilityfunctions';
import {useSmartEvidenceSourceId, useSmartEvidenceSourceEntityId} from '../../../../../../../util/Ares/customHooks';
import {labels} from '../../../../../../../util/utils';
import {useEngagementId} from '../../../../../../../util/customHooks';
import {getControlAttributes} from '../../../../../../../actions/ControlAttributes/controlattributesaction';
import ResourceWithLink from '../../../../../ResourceWithLink/ResourceWithLink';
import Loader from '../../../../../LoadingIndicator/LoadingIndicator';
import AttributesList from './ControlAttributesList';
import CreateEditControlAttributes from './CreateEditControlAttributes';
import ControlAttributesButton from './ControlAttributesButton';
import DeleteControlAttributes from './DeleteControlAttributes';

export default function ControlAttributes() {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const documentId = Utility.getDocumentIdFromUrl(window);
	const controlId = useSmartEvidenceSourceId(documentId);
	const controlEntityId = useSmartEvidenceSourceEntityId(documentId);
	const controlAttributes = useSelector((state) => state.controlAttributes);

	const [data, setData] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [attributeId, setAttributeId] = useState(null);
	const [showLoader, setShowLoader] = useState(true);
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	useEffect(() => {
		if (!controlId || !controlEntityId || controlEntityId != 19) {
			return setShowLoader(false);
		}

		fetchData();
	}, [controlId, controlEntityId]);

	useEffect(() => {
		if (controlAttributes.allIds) {
			const formattedData = controlAttributes.allIds.map((id) => ({...controlAttributes.byId[id], attributeId: id}));
			sortIntl(formattedData, 'attributeIndex');

			setData(formattedData);
			setShowLoader(false);
		}
	}, [controlAttributes]);

	const sortIntl = (arr = [], sortKey) => {
		arr.sort((a, b) => {
			let valueA = a[sortKey];
			let valueB = b[sortKey];

			// Both values are strings. Compare them in a case-insensitive manner.
			if (typeof valueA === 'string' && typeof valueB === 'string') {
				valueA = valueA.toLowerCase();
				valueB = valueB.toLowerCase();
			}

			return Intl.Collator('en-US').compare(valueA, valueB);
		});
	};

	const fetchData = async () => {
		try {
			await dispatch(getControlAttributes(engagementId, controlId, documentId));
		} catch {
			setShowLoader(false);
		}
	};

	const toggleCreateEditModal = (editAttributeId) => {
		setAttributeId(editAttributeId);
		setShowModal(!showModal);
	};

	const toggleDeleteModal = (attributeId) => {
		setAttributeId(attributeId);
		setShowDeleteModal(!showDeleteModal);
	};

	const NoControlAttributesContent = ({isLoading}) => {
		const content = data ? (
			<ResourceWithLink
				onPlaceholderClickConfig={{
					clickHere: () => {
						toggleCreateEditModal(null);
					}
				}}
				placeholder={labels.createControlAttributes}
			/>
		) : (
			labels.noControlAttributes
		);

		return (
			<NoControlAttributesTemplate className="NoControlAttributesTemplate">
				{isLoading ? <Loader variant="circular" /> : content}
			</NoControlAttributesTemplate>
		);
	};

	return (
		<>
			<Wrapper className="ControlAttributesWrapper">
				<Header className="ControlAttributesHeader">
					<h4>{labels.controlAttributes}</h4>
					{data && !showLoader && (
						<ControlAttributesButton
							handleOnClick={() => {
								toggleCreateEditModal(null);
							}}
						/>
					)}
				</Header>
				<Container className="ControlAttributesContainer">
					{showLoader || !data || data.length === 0 ? (
						<NoControlAttributesContent isLoading={showLoader} />
					) : (
						<AttributesList
							data={data}
							handleEditAttribute={toggleCreateEditModal}
							handleDeleteAttribute={toggleDeleteModal}
						/>
					)}
				</Container>
			</Wrapper>
			{showModal && (
				<CreateEditControlAttributes
					show
					onClose={() => {
						toggleCreateEditModal();
					}}
					engagementId={engagementId}
					attributeId={attributeId}
				/>
			)}
			{showDeleteModal && (
				<DeleteControlAttributes
					show
					onClose={() => {
						toggleDeleteModal();
					}}
					engagementId={engagementId}
					attributeId={attributeId}
				/>
			)}
		</>
	);
}

const Wrapper = styled.section`
	display: flex;
	flex-direction: column;
	border: var(--px-1) solid var(--neutrals-200);
	border-radius: var(--px-8) var(--px-8) var(--px-12) var(--px-12);
	overflow: hidden;
`;

const NoControlAttributesTemplate = styled.section`
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: var(--px-14);
	line-height: var(--px-24);
	text-align: center;
	color: var(--ui-text-secondary);
	margin: var(--px-24);
	min-height: var(--px-123);

	a.createClickableTextLink {
		font-weight: 400;
	}
`;

const Header = styled.section`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: var(--px-16) var(--px-24);
	border-bottom: var(--px-1) solid var(--neutrals-200);
	background: var(--neutrals-50);

	h4 {
		font-size: var(--px-16);
		line-height: var(--px-24);
		color: var(--neutrals-900);
		font-weight: 400;
		margin: 0;
	}
`;

const Container = styled.section`
	background: var(--white);
`;
